/**
 * Geocode Handler
 * Handle geocoding flow through Google Geocoding API
 */

import { state } from '../core/state.js';
import { updateCachedLocationWithGeocode } from './cache-updater.js';

/**
 * Handle geocoding for location input
 * @param {string} zip - Location query
 * @param {boolean} skipStaticData - Skip static data lookup (default: false)
 * @returns {Promise<Object|null>} - Geocode result or null
 */
export async function handleGeocoding(zip, skipStaticData = false) {
  // TODO: Extract from original file (part of url-parser.js)
  if (!zip) return null;

  console.log(`📍 Geocoding: "${zip}"`);

  // Try to get coordinates from static data first (cities.json/zips.json)
  if (!skipStaticData && window.LocationAutocomplete?.getCoordinatesFromStatic) {
    const staticCoords = window.LocationAutocomplete.getCoordinatesFromStatic(zip);

    if (staticCoords && staticCoords.lat && staticCoords.lng) {
      // Found in static data - use directly without API call
      console.log('✅ Using coordinates from static data:', staticCoords);
      state.coordinates = {
        lat: staticCoords.lat,
        lng: staticCoords.lng,
      };
      state.geocodeResult = {
        rawInput: zip,
        recognized: true,
        correctedLocationLabel: staticCoords.name || zip,
        lat: staticCoords.lat,
        lng: staticCoords.lng,
        // Coords + city/state for the decision-flow fallback: buildCenter reads
        // correctedLat/correctedLng and the state-expand step reads correctedState.
        // Without them a centerless "loc" step returns ALL camps ungeocoded
        // (geoDistance=0 → "0.0 mi away", unsorted).
        correctedLat: staticCoords.lat,
        correctedLng: staticCoords.lng,
        correctedCity: staticCoords.type === 'city' ? staticCoords.city : '',
        correctedState:
          staticCoords.type === 'state'
            ? staticCoords.abbr
            : staticCoords.state || '',
        correctedRegion:
          staticCoords.type === 'state' ? staticCoords.name : '',
      };

      // Don't show correction banner for static data
      state.showCorrectedLocationBanner = false;
      state.showUnrecognizedLocationBanner = false;

      // State-level search from static data: set state search flags
      // so performSearch applies state filter and skips radius
      if (staticCoords.type === 'state' && staticCoords.abbr) {
        state.isStateSearch = true;
        state.searchStateCode = staticCoords.abbr;
        state.searchStateName = staticCoords.name || staticCoords.abbr;
        state.isExcludeUSSearch = false;
        state.excludeUSRegionLabel = null;
        state.regionFilter = null;
        state.ukStateValue = null;
        console.log('🏛️ State detected from static data:', {
          code: state.searchStateCode,
          name: state.searchStateName,
        });
      } else {
        state.isStateSearch = false;
        state.searchStateCode = null;
        state.searchStateName = null;
      }

      // Static-data path skips the geo cache write, so the camps-helpers hook
      // never fires — log the location overwrite explicitly. Static data is
      // US-only; 'search' becomes 'listing' inside an embedded preset.
      // getCoordinatesFromStatic returns different fields per type:
      //   state → {name, abbr}; city → {city, state}; zip → {zip}
      const logFields = { city: null, region: null, zip: null };
      if (staticCoords.type === 'state') {
        logFields.region = staticCoords.name || staticCoords.abbr;
      } else if (staticCoords.type === 'city') {
        logFields.city = staticCoords.city;
        logFields.region = staticCoords.state;
      } else if (staticCoords.type === 'zip') {
        logFields.zip = staticCoords.zip;
      }
      window.CampsUtils?.logLocationEvent?.({
        source: 'search',
        fromCache: false,
        lat: staticCoords.lat,
        lng: staticCoords.lng,
        city: logFields.city,
        region: logFields.region,
        zip: logFields.zip,
        country: 'US',
      });

      return state.geocodeResult;
    } else {
      console.log('⚠️ Not found in static data, trying Google Geocoding API');
    }
  }

  // Use CampsUtils geocoding
  if (!window.CampsUtils?.geocodeZip) {
    console.warn('CampsUtils.geocodeZip not available');
    return null;
  }

  try {
    const result = await window.CampsUtils.geocodeZip(zip);

    // Europe/UK region (Europe, England, UK, Scotland, London): exclude US — show non-US camps only
    // Must be checked BEFORE lat/lng check, as UK regions may not have coordinates
    // (Mirrors handleEuropeUKGeocode() in url-parser.js)
    if (result && result.excludeUS) {
      console.log('🌍 Geocoding returned non-US location, using excludeUS flow:', result);
      state.isExcludeUSSearch = true;
      state.excludeUSRegionLabel = result.regionLabel || 'Europe';
      state.regionFilter = result.regionFilter || null;
      state.ukStateValue = result.stateValue || null;
      state.ukCountryFilter = result.ukCountryFilter || null;
      state.sortByDistanceNoRadius = result.sortByDistanceNoRadius || false;
      state.isStateSearch = false;
      state.searchStateCode = null;
      state.searchStateName = null;

      if (result.lat != null && result.lng != null) {
        state.coordinates = { lat: result.lat, lng: result.lng };
      } else {
        state.coordinates = null;
      }

      state.geocodeResult = {
        rawInput: zip,
        recognized: true,
        excludeUS: true,
        correctedLocationLabel: result.regionLabel || 'Europe',
      };
      state.showCorrectedLocationBanner = false;
      state.showUnrecognizedLocationBanner = false;

      return state.geocodeResult;
    }

    if (result && result.lat && result.lng) {
      state.coordinates = {
        lat: result.lat,
        lng: result.lng,
      };

      // Build geocodeResult with recognized: true — Google API successfully resolved the location.
      // This is required for getGeoSearchParams() to apply the search radius.
      // (Mirrors handleSuccessfulGeocode() in url-parser.js)
      state.geocodeResult = {
        ...result,
        rawInput: zip,
        recognized: true,
        correctedLocationLabel: result.city && result.state
          ? `${result.city}, ${result.state}`
          : result.city || result.region || zip,
      };

      // Detect state-level search: no city returned means Google resolved to a state
      const isStateInput = !(result.city || '').trim();
      if (isStateInput && (result.state || result.region)) {
        state.isStateSearch = true;
        state.searchStateCode = result.state
          || (window.CampsUtils?.mapStateNameToCode?.(result.region) || result.region);
        state.searchStateName = result.region || result.state;
        state.isExcludeUSSearch = false;
        state.regionFilter = null;
        state.ukStateValue = null;
        console.log('🏛️ State detected from Google API:', {
          code: state.searchStateCode,
          name: state.searchStateName,
        });
      } else {
        state.isStateSearch = false;
        state.searchStateCode = null;
        state.searchStateName = null;
      }

      state.showCorrectedLocationBanner = false;
      state.showUnrecognizedLocationBanner = false;

      // Update cache
      updateCachedLocationWithGeocode(result);

      console.log('✅ Geocoding successful:', result);
      return state.geocodeResult;
    } else {
      console.warn('Geocoding returned no results');
      return null;
    }
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

console.log('📦 Geocode handler module loaded');
